import Group from 'components/utils/Group'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import getLoyaltyUpgradeTargetDisplayName from 'loyalty/lib/benefits/getLoyaltyUpgradeTargetDisplayName'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

// The link follows the sentence, so it needs a word-space. A literal {' '} in JSX trips the
// no-literal-string-in-jsx rule, so the gap is styling rather than content.
const DetailsLink = styled(TextLink)`
  margin-inline-start: ${rem(4)};
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  margin-inline: ${rem(12)};
  padding: ${rem(12)} ${rem(16)};
  border-radius: ${rem(8)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  upgradeType: App.LoyaltyStayUpgradeType
  /**
   * Raw target name. A package name is trimmed to its upgrade descriptor for display; a room name is
   * already the bare room, so it is shown as-is rather than split on any dash it happens to contain.
   */
  targetName: string
  onApply: () => void
  onOpenDetails: () => void
}

/**
 * One upgrade offered inside the expanded strip: what it is, a link to the full detail, and Apply.
 *
 * Sits in the strip rather than the modal so a member who already knows what they want can apply
 * without opening anything.
 */
function LoyaltyStayUpgradeStripRow({ upgradeType, targetName, onApply, onOpenDetails }: Props) {
  const intl = useIntl()
  const displayName = upgradeType === 'package' ? getLoyaltyUpgradeTargetDisplayName(targetName) : targetName.trim()

  return <Row>
    <Group direction="horizontal" gap={12} verticalAlign="center">
      {upgradeType === 'package' ? <LineCrownIcon size="S" /> : <LineBedIcon size="S" />}
      <BodyText variant="medium">
        {upgradeType === 'package' && <FormattedMessage
          id="cGqI7vAGVht_VDIgqpQY"
          defaultMessage="<b>Upgrade package</b> to {name}."
          description="Row in the expanded free-upgrade strip naming the package a loyalty member can upgrade to"
          values={{ name: displayName, b: (chunks) => <b>{chunks}</b> }}
        />}
        {upgradeType === 'room' && <FormattedMessage
          id="PtJ4Vm18oqnn0Iyd41NE"
          defaultMessage="<b>Upgrade room</b> to {name}."
          description="Row in the expanded free-upgrade strip naming the room a loyalty member can upgrade to"
          values={{ name: displayName, b: (chunks) => <b>{chunks}</b> }}
        />}
        <DetailsLink weight="regular" size="medium" underline onClick={onOpenDetails}>
          {intl.formatMessage({
            id: 'KX4B1j_FX7527CJFmjJr',
            defaultMessage: 'Details',
            description: 'Link on the offer page room card that opens the loyalty free package upgrade details modal',
          })}
        </DetailsLink>
      </BodyText>
    </Group>
    {/* Member-exclusive offers wrap the tile in the lux-plus palette, which buttons inherit. Pin the
        brand default so this Apply does not turn up blue on those offers. */}
    <TextButton palette="default" kind="secondary" size="small" shape="round" onClick={onApply}>
      {intl.formatMessage({
        id: '52MnR5GMWaxXf5w0r-fr',
        defaultMessage: 'Apply',
        description: 'Button on the offer page room card that opens the loyalty free package upgrade modal',
      })}
    </TextButton>
  </Row>
}

export default LoyaltyStayUpgradeStripRow
